import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

interface Material {
  id: string;
  title: string;
  description: string;
  category: string;
  grade_level: string;
  price: number;
  thumbnail_url: string;
  page_count: number;
}

interface MaterialWithRating extends Material {
  averageRating?: number;
  reviewCount?: number;
}

export default function MaterialsPage() {
  const [materials, setMaterials] = useState<MaterialWithRating[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchMaterials();
  }, [selectedCategory, selectedGrade]);

  const fetchMaterials = async () => {
    setLoading(true);
    try {
      let url = 'https://fnhsumfewqfgpjteloga.supabase.co/functions/v1/get-materials?';
      if (selectedCategory !== 'all') url += `category=${selectedCategory}&`;
      if (selectedGrade !== 'all') url += `grade_level=${selectedGrade}`;

      const response = await fetch(url);
      const data = await response.json();
      const materialsData = data.materials || [];
      
      // 獲取每個教材的評分
      const materialsWithRatings = await Promise.all(
        materialsData.map(async (material: Material) => {
          try {
            const reviewResponse = await fetch(
              `https://fnhsumfewqfgpjteloga.supabase.co/functions/v1/get-reviews?material_id=${material.id}`
            );
            const reviewData = await reviewResponse.json();
            return {
              ...material,
              averageRating: reviewData.averageRating || 0,
              reviewCount: reviewData.total || 0,
            };
          } catch {
            return material;
          }
        })
      );
      
      setMaterials(materialsWithRatings);
    } catch (error) {
      console.error('獲取教材失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { value: 'all', label: '全部分類' },
    { value: '代數', label: '代數' },
    { value: '幾何', label: '幾何' },
    { value: '微積分', label: '微積分' },
    { value: '統計', label: '統計' },
    { value: '三角函數', label: '三角函數' },
  ];

  const grades = [
    { value: 'all', label: '全部年級' },
    { value: '幼稚園', label: '幼稚園' },
    { value: '小一', label: '小一' },
    { value: '小二', label: '小二' },
    { value: '小三', label: '小三' },
    { value: '小四', label: '小四' },
    { value: '小五', label: '小五' },
    { value: '小六', label: '小六' },
    { value: '中一', label: '中一' },
    { value: '中二', label: '中二' },
    { value: '中三', label: '中三' },
    { value: '中四', label: '中四' },
    { value: '中五', label: '中五' },
    { value: '中六', label: '中六' },
  ];

  const filteredMaterials = materials.filter(material =>
    material.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    material.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img src="https://public.readdy.ai/ai/img_res/46c4b423-33ec-4c59-b54c-70c60984f453.png" alt="Logo" className="h-10 w-10 object-contain" />
              <span className="text-xl font-bold text-gray-900">數學學習平台</span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/homework" className="text-gray-700 hover:text-orange-500 transition-colors font-medium">
                作業上傳
              </Link>
              <Link to="/admin" className="text-gray-700 hover:text-orange-500 transition-colors font-medium">
                管理後台
              </Link>
              <button className="px-6 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition-colors font-medium whitespace-nowrap cursor-pointer">
                我的帳戶
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-50 via-white to-orange-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center space-y-6 mb-12">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900">
              精選數學教材
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              專業編寫的數學教材，涵蓋各個年級與主題，助您輕鬆掌握數學知識
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <input
                type="text"
                placeholder="搜尋教材名稱或關鍵字..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-6 py-4 pr-12 border-2 border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base"
              />
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 flex items-center justify-center">
                <i className="ri-search-line text-2xl text-gray-400"></i>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 justify-center">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">分類：</span>
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => (
                  <button
                    key={cat.value}
                    onClick={() => setSelectedCategory(cat.value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      selectedCategory === cat.value
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">年級：</span>
              <div className="flex flex-wrap gap-2">
                {grades.map(grade => (
                  <button
                    key={grade.value}
                    onClick={() => setSelectedGrade(grade.value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      selectedGrade === grade.value
                        ? 'bg-blue-900 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {grade.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Materials Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
            </div>
          ) : filteredMaterials.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
                <i className="ri-file-search-line text-4xl text-gray-400"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">找不到相關教材</h3>
              <p className="text-gray-600">請嘗試調整搜尋條件或篩選器</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
              {filteredMaterials.map((material) => (
                <Link
                  key={material.id}
                  to={`/materials/${material.id}`}
                  className="group cursor-pointer"
                >
                  <div className="rounded-2xl overflow-hidden bg-gray-100 h-80 mb-4 relative">
                    <img
                      src={material.thumbnail_url || `https://public.readdy.ai/ai/img_res/40a3a2103e9f80f4d8b1784bcc224d9e.jpg$%7Bmaterial.category%7D%20$%7Bmaterial.grade_level%7D%20level%2C%20professional%20educational%20design%2C%20clean%20modern%20layout%2C%20soft%20background&width=300&height=400&seq=${material.id}&orientation=portrait`}
                      alt={material.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-gray-700">
                      {material.page_count} 頁
                    </div>
                    {material.reviewCount && material.reviewCount > 0 && (
                      <div className="absolute top-4 left-4 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full flex items-center space-x-1">
                        <i className="ri-star-fill text-yellow-500 text-sm"></i>
                        <span className="text-xs font-semibold text-gray-900">
                          {material.averageRating?.toFixed(1)}
                        </span>
                        <span className="text-xs text-gray-600">
                          ({material.reviewCount})
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-xs text-gray-600">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-md font-medium">
                        {material.grade_level}
                      </span>
                      <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-md font-medium">
                        {material.category}
                      </span>
                    </div>
                    <h3 className="font-semibold text-gray-900 line-clamp-2">{material.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2">{material.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-gray-900">HK$ {material.price}</span>
                      <button
                        onClick={() => navigate(`/materials/${material.id}`)}
                        className="px-6 py-2 bg-blue-900 text-white rounded-full font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        查看詳情
                      </button>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © 2025 數學學習平台. 版權所有
            </div>
            <div>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
